import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { verifierBotTelegram } from '../services/api';
import ParametreField, { type ParametreItem } from './ParametreField';

/**
 * Bot Telegram du comité : son jeton et son nom.
 *
 * Un jeton se recopie à la main depuis Telegram, et un caractère de travers
 * suffit à le rendre inutile — on ne s'en apercevrait qu'au premier message à
 * envoyer, c'est-à-dire au pire moment. Le bouton de vérification demande son
 * identité au bot : si Telegram répond, le jeton est bon, et l'écran dit quel
 * bot il ouvre.
 */
export default function BotTelegram({
  parametres,
  onSave,
  enCoursDeSauvegarde,
  cleSauvegardee,
}: {
  parametres: ParametreItem[];
  onSave: (cle: string, valeur: string | null) => void;
  enCoursDeSauvegarde: string | null;
  cleSauvegardee: string | null;
}) {
  const [resultat, setResultat] = useState<
    {
      ok: boolean;
      message: string;
      concorde?: boolean;
      canaux?: { canal: string; libelle: string; etat: string; message: string }[];
    } | null
  >(null);

  const verifier = useMutation({
    mutationFn: verifierBotTelegram,
    onSuccess: (d: any) =>
      setResultat({ ok: true, message: d.message, concorde: d.nomConcorde, canaux: d.canaux }),
    onError: (e: Error) => setResultat({ ok: false, message: e.message }),
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        {parametres.map((p) => (
          <ParametreField
            key={p.cle}
            parametre={p}
            onSave={onSave}
            isSaving={enCoursDeSauvegarde === p.cle}
            isSaved={cleSauvegardee === p.cle}
          />
        ))}
      </div>

      <div className="pt-4 border-t border-gray-100 dark:border-gray-800 space-y-3">
        <button
          type="button"
          onClick={() => {
            setResultat(null);
            verifier.mutate();
          }}
          disabled={verifier.isPending}
          className="btn-outline inline-flex items-center gap-2 disabled:opacity-50"
        >
          {verifier.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ShieldCheck className="w-4 h-4" />
          )}
          Vérifier le bot et les deux canaux
        </button>
        <p className="text-xs text-gray-500 dark:text-gray-400 -mt-1">
          Un message de test part réellement dans chaque canal : c'est la seule façon de savoir
          que le bot y est bien membre et autorisé à écrire.
        </p>

        {resultat && (
          <p
            className={`text-sm rounded-xl px-3 py-2 flex items-start gap-2 ${
              resultat.ok
                ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                : 'bg-red-50 text-red-700'
            }`}
          >
            {resultat.ok ? (
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            ) : (
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            )}
            {resultat.message}
          </p>
        )}

        {/* Le sort de chaque canal, séparément : « ça ne marche pas » ne dit
            pas lequel des deux, et c'est pourtant toute la question. */}
        {resultat?.canaux && resultat.canaux.length > 0 && (
          <ul className="space-y-1.5">
            {resultat.canaux.map((c) => (
              <li
                key={c.canal}
                className={`text-sm rounded-xl px-3 py-2 flex items-start gap-2 ${
                  c.etat === 'envoye'
                    ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                    : c.etat === 'absent'
                      ? 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                      : 'bg-amber-50 text-amber-900 dark:bg-amber-900/25 dark:text-amber-100'
                }`}
              >
                {c.etat === 'envoye' ? (
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                ) : (
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                )}
                <span>
                  <strong>{c.libelle}</strong> — {c.message}
                </span>
              </li>
            ))}
          </ul>
        )}

        {/* Un jeton d'un bot et le nom d'un autre : les messages partiraient
            d'un compte que personne ne reconnaîtrait. */}
        {resultat?.ok && resultat.concorde === false && (
          <p className="text-sm rounded-xl px-3 py-2 bg-amber-50 text-amber-900 dark:bg-amber-900/25 dark:text-amber-100 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            Le nom enregistré ne désigne pas ce bot. Corrigez-le pour qu'il désigne bien celui
            que le jeton ouvre.
          </p>
        )}

        <p className="text-xs text-gray-500 dark:text-gray-400">
          Le jeton est délivré par <span className="font-mono">@BotFather</span> dans Telegram.
          Il ouvre l'accès complet au bot : ne le communiquez pas.
        </p>
      </div>
    </div>
  );
}
